import { z } from 'zod';
import { clientReadSchema, isoDateSchema, isoInstantSchema } from './schemas.js';
import { householdFactFindDocumentStoredSchema, reviewHistorySchema } from './schemas-household.js';
/**
 * GOAL 2026-09-10 item 9 — the records a firm needs to LEAVE the service or
 * to reconstruct a case, as stored. This is evidence, not a view: stored
 * blobs (the fact-find document, scenario definitions, run inputs and
 * results) are exported exactly as persisted and typed loosely on purpose,
 * because a bundle must not fail to export because a five-year-old run
 * predates today's result contract. Every money figure inside is integer
 * pence as the engine produced it.
 */
export const EXPORT_FORMAT_VERSION = 1;
export const exportedScenarioSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    isBaseline: z.boolean(),
    status: z.string(),
    startYear: z.number().int(),
    projectionYears: z.number().int(),
    definition: z.unknown(),
    includeInReport: z.boolean(),
    visible: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
})
    .strict();
export const exportedRunSchema = z
    .object({
    runId: z.string().uuid(),
    auditRef: z.string(),
    asOfDate: isoDateSchema,
    startYear: z.number().int(),
    taxRulesVersion: z.string(),
    engineVersion: z.string(),
    codeVersion: z.string(),
    createdAt: isoInstantSchema,
    createdBy: z.string(),
    inputsSnapshot: z.unknown(),
    assumptionsSnapshot: z.unknown(),
    result: z.unknown(),
})
    .strict();
export const exportedReportSchema = z
    .object({
    exportId: z.string().uuid(),
    status: z.string(),
    runId: z.string().uuid().nullable(),
    attestedAt: isoInstantSchema.nullable(),
    createdAt: isoInstantSchema,
    file: z
        .object({
        id: z.string().uuid(),
        sha256: z.string(),
        sizeBytes: z.number().int(),
        contentType: z.string(),
        engineVersion: z.string(),
        taxRulesVersion: z.string(),
        createdAt: isoInstantSchema,
        /** Short-lived pre-signed URL; null when no object store is configured. */
        downloadUrl: z.string().nullable(),
    })
        .strict()
        .nullable(),
})
    .strict();
export const exportedChecklistTickSchema = z
    .object({
    item: z.string(),
    tickedBy: z.string(),
    tickedAt: isoInstantSchema,
    note: z.string().nullable(),
    retiredAt: isoInstantSchema.nullable(),
})
    .strict();
export const exportedAuditEntrySchema = z
    .object({
    occurredAt: isoInstantSchema,
    action: z.string(),
    adviserId: z.string().nullable(),
    entityType: z.string(),
    entityId: z.string().nullable(),
    after: z.unknown(),
})
    .strict();
export const clientExportSchema = z
    .object({
    formatVersion: z.literal(EXPORT_FORMAT_VERSION),
    exportedAt: isoInstantSchema,
    exportedBy: z.string(),
    codeVersion: z.string(),
    client: clientReadSchema,
    factFind: z
        .object({
        document: householdFactFindDocumentStoredSchema,
        version: z.number().int(),
        updatedAt: isoInstantSchema,
    })
        .nullable(),
    scenarios: z.array(exportedScenarioSchema),
    projectionRuns: z.array(exportedRunSchema),
    reviews: reviewHistorySchema.shape.reviews,
    checklist: z.array(exportedChecklistTickSchema),
    reports: z.array(exportedReportSchema),
    auditTrail: z.array(exportedAuditEntrySchema),
    disclaimer: z.string().min(1),
})
    .strict();
export const firmExportSchema = z
    .object({
    formatVersion: z.literal(EXPORT_FORMAT_VERSION),
    exportedAt: isoInstantSchema,
    exportedBy: z.string(),
    codeVersion: z.string(),
    firm: z.object({ id: z.string(), name: z.string(), createdAt: isoInstantSchema }).strict(),
    members: z.array(z
        .object({
        adviserId: z.string(),
        email: z.string(),
        displayName: z.string(),
        role: z.string(),
        status: z.string(),
    })
        .strict()),
    clients: z.array(clientExportSchema),
    disclaimer: z.string().min(1),
})
    .strict();
//# sourceMappingURL=export.js.map